export const PLANS = ['free', 'starter', 'pro', 'business'] as const;
export type Plan = (typeof PLANS)[number];

// Plan tiers — pricing & monthly quotas (CLAUDE.md §9). `maxAssistants: null`
// means unlimited. The message caps stay provisional until the beta gives us
// real Mistral API costs to calibrate against.
export interface PlanLimits {
  label: string;
  priceEur: number;
  messagesPerMonth: number;
  maxAssistants: number | null;
}

export const PLAN_LIMITS: Record<Plan, PlanLimits> = {
  free: { label: 'Free', priceEur: 0, messagesPerMonth: 200, maxAssistants: 1 },
  starter: { label: 'Starter', priceEur: 4.9, messagesPerMonth: 1500, maxAssistants: 3 },
  pro: { label: 'Pro', priceEur: 19.9, messagesPerMonth: 7000, maxAssistants: 10 },
  business: { label: 'Business', priceEur: 59, messagesPerMonth: 25000, maxAssistants: null },
} as const;

export const ASSISTANT_STATUSES = ['draft', 'active', 'paused', 'archived'] as const;
export type AssistantStatus = (typeof ASSISTANT_STATUSES)[number];

export const FALLBACK_ACTIONS = ['contact', 'silent', 'ask_contact'] as const;
export type FallbackAction = (typeof FALLBACK_ACTIONS)[number];

export const MESSAGE_ROLES = ['user', 'assistant', 'provider', 'system'] as const;
export type MessageRole = (typeof MESSAGE_ROLES)[number];

export const CONVERSATION_STATUSES = ['active', 'closed', 'flagged'] as const;
export type ConversationStatus = (typeof CONVERSATION_STATUSES)[number];

export const UNANSWERED_STATUSES = ['pending', 'answered', 'dismissed'] as const;
export type UnansweredStatus = (typeof UNANSWERED_STATUSES)[number];

export const DIMMITRI_ROLES = ['user', 'assistant', 'tool_call', 'tool_result'] as const;
export type DimmitriRole = (typeof DIMMITRI_ROLES)[number];

export const PENDING_ACTION_TYPES = [
  'create_assistant',
  'update_section',
  'answer_unanswered',
  'create_slug',
  'reassign_slug',
  'other',
] as const;
export type PendingActionType = (typeof PENDING_ACTION_TYPES)[number];

export const PENDING_ACTION_STATUSES = ['pending', 'applied', 'rejected', 'expired'] as const;
export type PendingActionStatus = (typeof PENDING_ACTION_STATUSES)[number];

export const LLM_PROVIDERS = ['mistral', 'anthropic', 'openai'] as const;
export type LLMProviderId = (typeof LLM_PROVIDERS)[number];

export const SLUG_LENGTH = 6;
export const SLUG_ALPHABET = 'abcdefghijkmnopqrstuvwxyz23456789'; // no 0/1/l for legibility

export const VISITOR_RATE_LIMITS = {
  burstMax: 5,
  burstWindowMs: 30_000,
  hourlyMax: 30,
  dailyMax: 100,
} as const;

export const SESSION_COOKIE_NAME = 'dimmi_session';
export const SESSION_MAX_AGE_SECONDS = 60 * 60 * 24 * 30; // 30 days

// ── Administration (CLAUDE.md §6 — supervision) ──────────────────────────
// The platform has a single hard-coded administrator in v1 — there is no
// `is_admin` column. An account is admin iff its (normalized) email matches.
export const ADMIN_EMAIL = 'vinduprez@me.com';

export function isAdminEmail(email: string): boolean {
  return email.trim().toLowerCase() === ADMIN_EMAIL;
}

// Impersonation ("ouvrir le compte d'un client") is carried by a short-lived
// bearer token kept in the new tab's sessionStorage — never a cookie — so it
// cannot collide with the admin's own cookie session in other tabs.
export const IMPERSONATION_MAX_AGE_SECONDS = 60 * 60 * 8; // 8 hours
// Hash-fragment key the impersonation tab is opened with, and the per-tab
// sessionStorage key it is then parked under (fragments never reach the server).
export const IMPERSONATION_HASH_KEY = 'imp';
export const IMPERSONATION_STORAGE_KEY = 'dimmi_imp_token';

// ── Visitor chat ─────────────────────────────────────────────────────────
// Marker the LLM prepends to an answer it isn't confident about (CLAUDE.md §7).
// Stripped before display, used to route the question to `unanswered_questions`.
export const UNANSWERED_MARKER = '[UNANSWERED]';

// Hard cap on a single visitor message — guards both the DB and the token bill.
export const VISITOR_MESSAGE_MAX_LENGTH = 4000;

// localStorage key holding the visitor's anonymous UUID (no account in v1).
export const VISITOR_UUID_STORAGE_KEY = 'dimmi_visitor_uuid';

// A returning visitor whose last message is older than this starts a fresh
// conversation rather than resuming a stale thread (CLAUDE.md §5).
export const VISITOR_CONVERSATION_STALE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// Socket.io namespace + event names for the visitor ↔ assistant channel.
export const VISITOR_NAMESPACE = '/visitor';
export const VISITOR_EVENTS = {
  send: 'message:send', // client → server : { slug, visitorUuid, content }
  subscribe: 'conversation:subscribe', // client → server : { slug, visitorUuid }
  chunk: 'message:chunk', // server → client : { delta }
  done: 'message:done', // server → client : VisitorDonePayload
  ack: 'message:ack', // server → client : VisitorAckPayload (AI paused — live takeover)
  error: 'message:error', // server → client : { code, message }
  takeoverStarted: 'takeover:started', // server → client : TakeoverStartedPayload
  takeoverEnded: 'takeover:ended', // server → client : TakeoverEndedPayload
  providerMessage: 'provider:message', // server → client : ProviderReplyPayload
} as const;

// Socket.io `/provider` namespace — the dashboard live-takeover channel
// (CLAUDE.md §5/§6). Authenticated server-side from the session cookie at
// handshake time. Joining a conversation pauses the assistant LLM.
export const PROVIDER_NAMESPACE = '/provider';
export const PROVIDER_EVENTS = {
  join: 'conversation:join', // client → server : { conversationId } + ack callback
  leave: 'conversation:leave', // client → server : { conversationId } + ack callback
  visitorMessage: 'visitor:message', // server → client : ProviderVisitorMessagePayload
} as const;

// Conversation list pagination (provider dashboard).
export const CONVERSATIONS_PAGE_SIZE = 30;
export const CONVERSATIONS_PAGE_MAX = 100;

// ── Provider dashboard (overview) ────────────────────────────────────────
// How many recent conversations the dashboard home screen lists.
export const DASHBOARD_RECENT_LIMIT = 5;
// A conversation counts as "live" on the dashboard hero while it is `active`
// and its last message landed within this window.
export const DASHBOARD_LIVE_WINDOW_MS = 15 * 60 * 1000;

// Hard cap on a single provider message sent during a live takeover.
export const PROVIDER_MESSAGE_MAX_LENGTH = 4000;
